import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus, ChevronLeft, ChevronRight, Search, X, FolderOpen } from "lucide-react";
import { useIcertiSearch } from "./useIcertiSearch";
import CrearExpedienteDialog from "./CrearExpedienteDialog";
import TechDataDialog from "./TechDataDialog";
import AppointmentFormDialog from "./AppointmentFormDialog";
import AppointmentDetailPanel from "./AppointmentDetailPanel";
import { EQUIPE_COLORS } from "./planningConstants";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Card } from "../ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Remember the last viewed date/mode across page reloads (per browser --
// same pattern already used for the sidebar-collapsed preference). Without
// this, refreshing the page (e.g. after updating a dossier's DNI-extracted
// name and needing a reload to see it) always jumps back to today.
const _loadStoredDate = () => {
  const stored = localStorage.getItem("planning_current_date");
  if (stored) {
    const d = new Date(stored);
    if (!isNaN(d.getTime())) return d;
  }
  return new Date();
};

const PlanningV2 = ({ user: currentUser }) => {
  const [appointments, setAppointments] = useState([]);
  const [currentDate, setCurrentDate] = useState(_loadStoredDate);
  const [viewMode, setViewMode] = useState(() => localStorage.getItem("planning_view_mode") || "week"); // day, week, month
  const [selectedAppointment, setSelectedAppointment] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [dossiers, setDossiers] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingAppointment, setEditingAppointment] = useState(null);
  const [filterVendeur, setFilterVendeur] = useState("");
  const [filterSource, setFilterSource] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterEquipe, setFilterEquipe] = useState("");
  const [showTechDataDialog, setShowTechDataDialog] = useState(null); // appointment_id or null
  const [confirmCreateDossier, setConfirmCreateDossier] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const fetchAbortRef = useRef(null);       // Fix 1: cancel stale appointment fetches
  const [newAppt, setNewAppt] = useState({
    client_name: "", client_prenom: "", date: "", start_time: "10:00", end_time: "11:00",
    address: "", phone: "", email: "", notes: "", dossier_id: "", color: "#3b82f6",
    status: "Instalación programada", source: "", vendeur: "", ref_cadastre: "",
    type_logement: "", superficie: "", m2_instalacion: "", date_pose: "", equipe: "equipe1"
  });

  useEffect(() => {
    localStorage.setItem("planning_current_date", currentDate.toISOString());
  }, [currentDate]);

  useEffect(() => {
    localStorage.setItem("planning_view_mode", viewMode);
  }, [viewMode]);

  // Dynamic hours: adapt to actual appointment times so no RDV is ever hidden
  const HOURS = (() => {
    const times = appointments.map(a => parseInt(a.start_time?.split(":")[0] || "8", 10)).filter(h => !isNaN(h));
    const minH = times.length ? Math.min(...times) : 8;
    const maxH = times.length ? Math.max(...times) : 20;
    const start = Math.max(0,  Math.min(minH - 1, 7));   // at least 7h, extend down if needed
    const end   = Math.min(23, Math.max(maxH + 1, 21));  // at least 21h, extend up if needed
    return Array.from({ length: end - start + 1 }, (_, i) => i + start);
  })();

  const getWeekDays = (date) => {
    const d = new Date(date);
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    const monday = new Date(d.setDate(diff));
    return Array.from({ length: 7 }, (_, i) => {
      const dt = new Date(monday);
      dt.setDate(monday.getDate() + i);
      return dt;
    });
  };

  const weekDays = getWeekDays(currentDate);
  const dayNames = ["lu", "ma", "mi", "ju", "vi", "sá", "do"];
  const monthNames = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

  const formatDateKey = (d) => {
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };

  const fetchAppointments = useCallback(async () => {
    // Fix 1: cancel any previous in-flight request before starting a new one
    if (fetchAbortRef.current) fetchAbortRef.current.abort();
    const controller = new AbortController();
    fetchAbortRef.current = controller;

    setLoading(true);
    try {
      let startDate, endDate;
      if (viewMode === "day") {
        startDate = formatDateKey(currentDate);
        endDate = startDate;
      } else if (viewMode === "month") {
        const first = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
        const last = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
        startDate = formatDateKey(first);
        endDate = formatDateKey(last);
      } else {
        startDate = formatDateKey(weekDays[0]);
        endDate = formatDateKey(weekDays[6]);
      }
      const res = await axios.get(
        `${API}/appointments?start_date=${startDate}&end_date=${endDate}`,
        { signal: controller.signal }
      );
      setAppointments(res.data);
    } catch (err) {
      if (axios.isCancel(err) || err?.name === "CanceledError") return; // requête annulée, ignorer
      console.error("Error fetching appointments:", err);
    } finally {
      if (!controller.signal.aborted) setLoading(false);
    }
  }, [currentDate, viewMode]);

  const fetchDossiers = useCallback(async () => {
    try {
      // Fix 3: raised limit to 5000 to avoid silent truncation of dossier dropdown
      const res = await axios.get(`${API}/dossiers?limit=5000`);
      const list = res.data.dossiers || res.data;
      if (list.length >= 5000) console.warn("[Planning] Dossier list may be truncated (>=5000)");
      setDossiers(list);
    } catch (err) {
      console.error("[Planning] Error fetching dossiers:", err);
    }
  }, []);

  useEffect(() => { fetchAppointments(); }, [fetchAppointments]);

  // Keep the open detail panel in sync with the appointments list, instead of
  // it silently going stale or disappearing: after any refresh (Actualizar,
  // iCerti send, etc.) the same appointment's fresh data replaces the old
  // object, and a page reload restores whichever appointment was open
  // (persisted the same way as the current date/view mode above).
  //
  // restoredSelectionRef guards against a real race: on first mount
  // selectedAppointment is null (nothing selected YET, restore pending) and
  // appointments is still []. Without this flag, the persistence effect below
  // reads that initial null and deletes the just-saved localStorage entry
  // before the appointments ever finish loading and this effect gets a
  // chance to read it back -- so a reload always lost the selection.
  const restoredSelectionRef = useRef(false);
  useEffect(() => {
    if (!appointments.length) return;
    if (selectedAppointment) {
      const fresh = appointments.find(a => a.id === selectedAppointment.id);
      if (fresh) setSelectedAppointment(fresh);
      restoredSelectionRef.current = true;
      return;
    }
    if (restoredSelectionRef.current) return;
    restoredSelectionRef.current = true;
    const storedId = localStorage.getItem("planning_selected_appointment");
    if (storedId) {
      const found = appointments.find(a => a.id === storedId);
      if (found) setSelectedAppointment(found);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [appointments]);

  useEffect(() => {
    if (!restoredSelectionRef.current) return; // don't touch storage until the restore attempt above has run
    if (selectedAppointment) localStorage.setItem("planning_selected_appointment", selectedAppointment.id);
    else localStorage.removeItem("planning_selected_appointment");
  }, [selectedAppointment?.id]);
  useEffect(() => { fetchDossiers(); }, [fetchDossiers]);

  const navigateWeek = (dir) => {
    const d = new Date(currentDate);
    if (viewMode === "day") {
      d.setDate(d.getDate() + dir);
    } else if (viewMode === "month") {
      d.setMonth(d.getMonth() + dir);
    } else {
      d.setDate(d.getDate() + dir * 7);
    }
    setCurrentDate(d);
  };

  const goToday = () => setCurrentDate(new Date());

  // Cross-check the cadastral reference against every other appointment BEFORE
  // saving -- catches duplicates as early as possible, before a dossier is even
  // created. Non-blocking: just a confirm the user can accept if it's a
  // legitimate case. Returns false if the user cancels.
  const confirmDuplicateCadastre = async (excludeId) => {
    if (!newAppt.ref_cadastre?.trim()) return true;
    try {
      const dupRes = await axios.get(`${API}/appointments/check-cadastre`, {
        params: { ref_cadastre: newAppt.ref_cadastre, exclude_id: excludeId },
      });
      const duplicates = dupRes.data?.duplicates || [];
      if (duplicates.length > 0) {
        const list = duplicates.map(d => `- ${d.client_name} (${d.date}, ${d.status})`).join("\n");
        return window.confirm(
          `Atención: esta referencia catastral ya existe en otro(s) RDV:\n\n${list}\n\n¿Continuar de todas formas?`
        );
      }
    } catch {
      // Non-critical check -- if it fails, don't block the save
    }
    return true;
  };

  const handleCreate = async () => {
    if (!newAppt.client_name || !newAppt.date || !newAppt.start_time) {
      toast.error("Nombre, fecha y hora son obligatorios");
      return;
    }
    // Commerciales must capture cadastre/phone/email on the spot -- these
    // arrive late (or never) otherwise, since the tech/office side of the
    // pipeline assumes they're already on the appointment.
    if (currentUser?.role === "commercial") {
      const missing = [];
      if (!newAppt.ref_cadastre?.trim()) missing.push("Referencia Catastral");
      if (!newAppt.phone?.trim()) missing.push("Teléfono");
      if (!newAppt.email?.trim()) missing.push("Correo electrónico");
      if (missing.length) {
        toast.error(`Obligatorio para comerciales: ${missing.join(", ")}`);
        return;
      }
    }
    if (isSubmitting) return;
    if (!(await confirmDuplicateCadastre())) return;
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/appointments`, newAppt);
      toast.success("Cita creada");
      setShowCreateDialog(false);
      setNewAppt({ client_name: "", client_prenom: "", date: "", start_time: "10:00", end_time: "11:00", address: "", city: "", postal_code: "", phone: "", email: "", notes: "", dossier_id: "", color: "#3b82f6", status: "Instalación programada", source: "", vendeur: "", ref_cadastre: "", type_logement: "", superficie: "", m2_instalacion: "", date_pose: "", equipe: "equipe1" });
      fetchAppointments();
    } catch (err) {
      toast.error("Error al crear cita");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/appointments/${id}`);
      toast.success("Cita eliminada");
      setSelectedAppointment(null);
      fetchAppointments();
    } catch { toast.error("Error al eliminar"); }
  };

  const openEditDialog = (appt) => {
    setEditingAppointment(appt);
    setNewAppt({
      client_name: appt.client_name || "",
      client_prenom: appt.client_prenom || "",
      date: appt.date || "",
      start_time: appt.start_time || "10:00",
      end_time: appt.end_time || "11:00",
      address: appt.address || "",
      city: appt.city || "",
      postal_code: appt.postal_code || "",
      phone: appt.phone || "",
      email: appt.email || "",
      notes: appt.notes || "",
      dossier_id: appt.dossier_id || "",
      color: appt.color || "#3b82f6",
      status: appt.status || "Instalación programada",
      source: appt.source || "",
      vendeur: appt.vendeur || "",
      ref_cadastre: appt.ref_cadastre || "",
      type_logement: appt.type_logement || "",
      superficie: appt.superficie || "",
      m2_instalacion: appt.m2_instalacion || "",
      date_pose: appt.date_pose || "",
      equipe: appt.equipe || "equipe1",
    });
    setShowCreateDialog(true);
  };

  const handleUpdate = async () => {
    if (!newAppt.client_name || !newAppt.date || !newAppt.start_time) {
      toast.error("Nombre, fecha y hora son obligatorios");
      return;
    }
    if (isSubmitting) return;

    if (!(await confirmDuplicateCadastre(editingAppointment.id))) return;

    setIsSubmitting(true);
    try {
      await axios.put(`${API}/appointments/${editingAppointment.id}`, newAppt);
      toast.success("Cita actualizada");
      setShowCreateDialog(false);
      setEditingAppointment(null);
      // Keep the detail panel open on the same appointment -- the
      // appointments-sync effect below refreshes it with the saved data as
      // soon as fetchAppointments() resolves, instead of closing the panel.
      fetchAppointments();
    } catch (err) {
      toast.error("Error al actualizar");
    } finally {
      setIsSubmitting(false);
    }
  };


  // An iCerti order number typed in the search box may belong to another week: jump to it.
  useIcertiSearch(searchQuery, (appt) => {
    setCurrentDate(new Date(`${appt.date}T12:00:00`));
    setSelectedAppointment(appt);
  });

  const filteredAppointments = appointments.filter(a => {
    if (searchQuery.trim()) {
      const q = searchQuery.toLowerCase();
      const matchText = (a.client_name || "").toLowerCase().includes(q)
        || (a.phone || "").toLowerCase().includes(q)
        || (a.ref_cadastre || "").toLowerCase().includes(q)
        || (a.email || "").toLowerCase().includes(q)
        || (a.icerti?.pedido?.numero || "").toLowerCase().includes(q)
        || (a.icerti?.pedido?.expediente || "").toLowerCase().includes(q);
      if (!matchText) return false;
    }
    if (filterVendeur && a.vendeur !== filterVendeur) return false;
    if (filterSource && a.source !== filterSource) return false;
    if (filterStatus === "_has_dossier" && !a.dossier_id) return false;
    else if (filterStatus && filterStatus !== "_has_dossier" && a.status !== filterStatus) return false;
    if (filterType && a.type_logement !== filterType) return false;
    if (filterEquipe && (a.equipe || "equipe1") !== filterEquipe) return false;
    return true;
  });

  const withOverlapInfo = (appts) => {
    const items = appts.map(a => {
      const [h, m] = (a.start_time || "0:0").split(":").map(Number);
      const top = (h - HOURS[0]) * 56 + (m / 60) * 56;
      return { ...a, _top: top, _h: h };
    }).filter(a => a._top >= 0 && a._h >= HOURS[0] && a._h <= HOURS[HOURS.length - 1]);
    items.sort((a, b) => a._top - b._top);
    const groups = [];
    for (const item of items) {
      let placed = false;
      for (const group of groups) {
        if (group.some(g => Math.abs(g._top - item._top) < 28)) {
          group.push(item);
          placed = true;
          break;
        }
      }
      if (!placed) groups.push([item]);
    }
    const result = [];
    for (const group of groups) {
      const total = group.length;
      group.forEach((item, idx) => result.push({ ...item, _ci: idx, _ct: total }));
    }
    return result;
  };

  const isToday = (d) => {
    const today = new Date();
    return d.getDate() === today.getDate() && d.getMonth() === today.getMonth() && d.getFullYear() === today.getFullYear();
  };

  const openCreateForSlot = (dayDate, hour) => {
    setEditingAppointment(null);
    setNewAppt({
      client_name: "", client_prenom: "", dossier_id: "", color: "#3b82f6",
      address: "", phone: "", email: "", notes: "",
      status: "Instalación programada", source: "", vendeur: "", ref_cadastre: "",
      type_logement: "", superficie: "", m2_instalacion: "", date_pose: "",
      equipe: "equipe1",
      date: formatDateKey(dayDate),
      start_time: `${String(hour).padStart(2, '0')}:00`,
      end_time: `${String(hour + 1).padStart(2, '0')}:00`,
    });
    setShowCreateDialog(true);
  };

  const weekStart = weekDays[0];
  const weekEnd = weekDays[6];
  const dayNamesFull = ["Domingo", "Lunes", "Martes", "Miércoles", "Jueves", "Viernes", "Sábado"];

  const headerText = viewMode === "day"
    ? `${dayNamesFull[currentDate.getDay()]} ${currentDate.getDate()} de ${monthNames[currentDate.getMonth()]} ${currentDate.getFullYear()}`
    : viewMode === "month"
    ? `${monthNames[currentDate.getMonth()].charAt(0).toUpperCase() + monthNames[currentDate.getMonth()].slice(1)} ${currentDate.getFullYear()}`
    : `${monthNames[weekStart.getMonth()].charAt(0).toUpperCase() + monthNames[weekStart.getMonth()].slice(1)} ${String(weekStart.getDate()).padStart(2, '0')} - ${weekEnd.getMonth() !== weekStart.getMonth() ? monthNames[weekEnd.getMonth()].charAt(0).toUpperCase() + monthNames[weekEnd.getMonth()].slice(1) + ' ' : ''}${String(weekEnd.getDate()).padStart(2, '0')}, ${weekEnd.getFullYear()}`;

  return (
    <div className="flex flex-col gap-3 h-full min-h-0 animate-fade-in-up" data-testid="planning-page">
      {/* Toolbar — tout en une ligne compacte */}
      <div className="flex items-center gap-2 flex-wrap shrink-0">
        {/* Titre compact */}
        <span className="text-base font-bold text-slate-800 mr-1">Planning</span>

        {/* View toggle */}
        <div className="flex bg-slate-100 rounded-lg p-0.5">
          {["day", "week", "month"].map(mode => (
            <button key={mode} onClick={() => setViewMode(mode)}
              className={`px-2.5 py-1 text-xs font-medium rounded-md transition-colors ${viewMode === mode ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-500 hover:text-slate-700'}`}
              data-testid={`view-${mode}`}>
              {mode === "day" ? "Día" : mode === "week" ? "Semana" : "Mes"}
            </button>
          ))}
        </div>

        {/* Navigation */}
        <div className="flex items-center gap-0.5">
          <button onClick={() => navigateWeek(-1)} className="p-1 rounded hover:bg-slate-100" data-testid="prev-week"><ChevronLeft size={16} /></button>
          <span className="text-xs font-medium text-slate-700 min-w-[150px] text-center capitalize">{headerText}</span>
          <button onClick={() => navigateWeek(1)} className="p-1 rounded hover:bg-slate-100" data-testid="next-week"><ChevronRight size={16} /></button>
        </div>
        <button onClick={goToday} className="text-xs font-medium text-blue-600 hover:text-blue-800" data-testid="go-today">Hoy</button>

        {/* Search */}
        <div className="relative">
          <Search size={13} className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
            placeholder="Nombre, tel, cadastro, pedido..." className="pl-7 h-7 w-44 text-xs"
            data-testid="planning-search" />
          {searchQuery && (
            <button onClick={() => setSearchQuery("")} className="absolute right-1.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"><X size={12} /></button>
          )}
        </div>

        <span className="text-xs text-slate-400 ml-1">{filteredAppointments.length} cita{filteredAppointments.length !== 1 ? 's' : ''}</span>

        {/* Nueva Cita */}
        <Button size="sm" className="ml-auto h-7 text-xs px-3" onClick={() => { setEditingAppointment(null); setNewAppt({ client_name: "", client_prenom: "", date: formatDateKey(new Date()), start_time: "10:00", end_time: "11:00", address: "", phone: "", email: "", notes: "", dossier_id: "", color: "#3b82f6", status: "Instalación programada", source: "", vendeur: "", ref_cadastre: "", type_logement: "", superficie: "", m2_instalacion: "", date_pose: "", equipe: "equipe1" }); setShowCreateDialog(true); }} data-testid="add-appointment-btn">
          <Plus className="w-3 h-3 mr-1" /> Nueva Cita
        </Button>
      </div>

      {/* Filtros — ligne compacte */}
      <div className="flex items-center gap-1.5 flex-wrap shrink-0">
        <Select value={filterVendeur} onValueChange={v => setFilterVendeur(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-7 w-36 text-xs" data-testid="filter-vendeur">
            <SelectValue placeholder="Vendedor" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todos</SelectItem>
            <SelectItem value="Andrea">Andrea</SelectItem>
            <SelectItem value="Vanessa">Vanessa</SelectItem>
            <SelectItem value="Anna">Anna</SelectItem>
            <SelectItem value="Meru">Meru</SelectItem>
            <SelectItem value="Silvia">Silvia</SelectItem>
            <SelectItem value="Equipe 1">Equipe 1</SelectItem>
            <SelectItem value="Equipe 2">Equipe 2</SelectItem>
            <SelectItem value="Equipe 3">Equipe 3</SelectItem>
            <SelectItem value="Parrainage">Parrainage</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterSource} onValueChange={v => setFilterSource(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-7 w-40 text-xs" data-testid="filter-source">
            <SelectValue placeholder="Fuente" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todas</SelectItem>
            <SelectItem value="Google ADS">Google ADS</SelectItem>
            <SelectItem value="Facebook & Instagram">Facebook & Instagram</SelectItem>
            <SelectItem value="Tik Tok ADS">Tik Tok ADS</SelectItem>
            <SelectItem value="Parrainage">Parrainage</SelectItem>
            <SelectItem value="Appel entrant">Appel entrant</SelectItem>
            <SelectItem value="Terrain (porte à porte)">Terrain (porte à porte)</SelectItem>
            <SelectItem value="Autre">Autre</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterStatus} onValueChange={v => setFilterStatus(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-7 w-44 text-xs" data-testid="filter-status">
            <SelectValue placeholder="Estado" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todos</SelectItem>
            <SelectItem value="Instalación programada">Instalación programada</SelectItem>
            <SelectItem value="Instalado">Instalado</SelectItem>
            <SelectItem value="Validado">Validado</SelectItem>
            <SelectItem value="Anulado">Anulado</SelectItem>
            <SelectItem value="_has_dossier">Expediente creado</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterType} onValueChange={v => setFilterType(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-7 w-32 text-xs" data-testid="filter-type">
            <SelectValue placeholder="Vivienda" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todos</SelectItem>
            <SelectItem value="apartamento">Apartamento</SelectItem>
            <SelectItem value="casa">Casa</SelectItem>
            <SelectItem value="local">Local</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filterEquipe} onValueChange={v => setFilterEquipe(v === "_all" ? "" : v)}>
          <SelectTrigger className="h-7 w-32 text-xs" data-testid="filter-equipe">
            <SelectValue placeholder="Equipo técnico" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="_all">Todos</SelectItem>
            <SelectItem value="equipe1">Equipo 1</SelectItem>
            <SelectItem value="equipe2">Equipo 2</SelectItem>
            <SelectItem value="equipe3">Equipo 3</SelectItem>
          </SelectContent>
        </Select>
        {(filterVendeur || filterSource || filterStatus || filterType || filterEquipe) && (
          <button onClick={() => { setFilterVendeur(""); setFilterSource(""); setFilterStatus(""); setFilterType(""); setFilterEquipe(""); }}
            className="text-xs text-red-500 hover:text-red-700 font-medium">
            ✕ Limpiar
          </button>
        )}
      </div>

      <div className="flex gap-4 flex-1 min-h-0">
        {/* Calendar Grid */}
        <Card className="flex-1 overflow-hidden flex flex-col">
          <div className="overflow-auto flex-1">
            {viewMode === "week" && (
            <div className="relative" data-testid="planning-grid">
              {/* Header row */}
              <div className="flex sticky top-0 z-10 bg-white border-b-2 border-slate-400">
                <div className="w-16 shrink-0 border-r border-slate-400" />
                {weekDays.map((d, i) => (
                  <div key={i} className={`flex-1 p-2 text-center border-r border-slate-400 ${isToday(d) ? 'bg-sky-100' : ''}`}>
                    <div className={`text-lg font-semibold ${isToday(d) ? 'text-blue-600' : 'text-slate-800'}`}>{d.getDate()}</div>
                    <div className={`text-xs ${isToday(d) ? 'text-blue-500 font-medium' : 'text-slate-400'}`}>{dayNames[i]}</div>
                  </div>
                ))}
              </div>
              {/* Time grid */}
              <div className="flex pt-3">
                {/* Hour labels */}
                <div className="w-16 shrink-0">
                  {HOURS.map(hour => (
                    <div key={hour} className="h-14 relative border-r border-slate-400">
                      <span className="absolute -top-2.5 right-2 text-xs font-medium text-slate-500 bg-white px-0.5">{hour}:00</span>
                    </div>
                  ))}
                </div>
                {/* Day columns */}
                {weekDays.map((d, di) => {
                  const dateKey = formatDateKey(d);
                  const dayAppts = filteredAppointments.filter(a => a.date === dateKey);
                  return (
                    <div key={di} className={`flex-1 relative border-r border-slate-400 ${isToday(d) ? 'bg-sky-100/50' : ''}`}>
                      {/* Hour + half-hour grid lines */}
                      {HOURS.map(hour => (
                        <div key={hour} className="h-14 relative">
                          <div className="absolute inset-x-0 top-0 border-t border-slate-400" />
                          <div className="absolute inset-x-0 top-1/2 border-t border-slate-400" />
                          <div className="absolute inset-0 top-0 h-7 cursor-pointer hover:bg-slate-50/80"
                            onClick={() => openCreateForSlot(d, hour)} />
                          <div className="absolute inset-0 top-7 h-7 cursor-pointer hover:bg-slate-50/80"
                            onClick={() => openCreateForSlot(d, hour)} />
                        </div>
                      ))}
                      {/* Appointments positioned absolutely — side by side if overlapping */}
                      {withOverlapInfo(dayAppts).map(a => (
                          <div key={a.id}
                            onClick={(e) => { e.stopPropagation(); setSelectedAppointment(a); }}
                            className={`absolute rounded px-1 py-0.5 cursor-pointer text-white text-[11px] leading-tight truncate hover:opacity-90 transition-opacity shadow-sm ${selectedAppointment?.id === a.id ? 'z-[6]' : 'z-[5]'}`}
                            style={{ backgroundColor: a.status === 'Anulado' ? '#ef4444' : a.status === 'Instalado' ? '#22c55e' : a.status === 'Validado' ? '#15803d' : (a.color || '#3b82f6'), borderLeft: `8px solid ${EQUIPE_COLORS[a.equipe || 'equipe1']}`, top: `${a._top}px`, height: '26px', left: `${(a._ci / a._ct) * 100}%`, width: `calc(${100 / a._ct}% - 2px)`, boxShadow: selectedAppointment?.id === a.id ? '0 0 0 3px #0f172a' : undefined }}
                            data-testid={`appt-${a.id}`}>
                            {(a.status === 'Instalado' || a.status === 'Validado') && <span>&#10003; </span>}
                            {a.dossier_id && <FolderOpen size={11} className="inline-block mr-0.5 align-text-top" style={{ color: '#fbbf24' }} fill="#fbbf24" />}
                            <span className="font-medium">{a.client_name}</span>
                            <span className="opacity-80 ml-1">{a.start_time}</span>
                          </div>
                      ))}
                    </div>
                  );
                })}
              </div>
            </div>
            )}

            {viewMode === "day" && (() => {
              const dateKey = formatDateKey(currentDate);
              const dayAppts = filteredAppointments.filter(a => a.date === dateKey);
              return (
              <div className="relative" data-testid="planning-grid-day">
                {/* Header */}
                <div className="flex sticky top-0 z-10 bg-white border-b-2 border-slate-400">
                  <div className="w-16 shrink-0 border-r border-slate-400" />
                  <div className={`flex-1 p-2 text-center ${isToday(currentDate) ? 'bg-sky-100' : ''}`}>
                    <div className={`text-lg font-semibold ${isToday(currentDate) ? 'text-blue-600' : 'text-slate-800'}`}>{currentDate.getDate()}</div>
                    <div className={`text-xs ${isToday(currentDate) ? 'text-blue-500 font-medium' : 'text-slate-400'}`}>{dayNamesFull[currentDate.getDay()]}</div>
                  </div>
                </div>
                {/* Time grid */}
                <div className="flex pt-3">
                  <div className="w-16 shrink-0">
                    {HOURS.map(hour => (
                      <div key={hour} className="h-14 relative border-r border-slate-400">
                        <span className="absolute -top-2.5 right-2 text-xs font-medium text-slate-500 bg-white px-0.5">{hour}:00</span>
                      </div>
                    ))}
                  </div>
                  <div className={`flex-1 relative ${isToday(currentDate) ? 'bg-sky-100/50' : ''}`}>
                    {HOURS.map(hour => (
                      <div key={hour} className="h-14 relative">
                        <div className="absolute inset-x-0 top-0 border-t border-slate-400" />
                        <div className="absolute inset-x-0 top-1/2 border-t border-slate-400" />
                        <div className="absolute inset-0 top-0 h-7 cursor-pointer hover:bg-slate-50/80"
                          onClick={() => openCreateForSlot(currentDate, hour)} />
                        <div className="absolute inset-0 top-7 h-7 cursor-pointer hover:bg-slate-50/80"
                          onClick={() => openCreateForSlot(currentDate, hour)} />
                      </div>
                    ))}
                    {withOverlapInfo(dayAppts).map(a => (
                      <div key={a.id}
                          onClick={(e) => { e.stopPropagation(); setSelectedAppointment(a); }}
                          className={`absolute rounded px-2 py-0.5 cursor-pointer text-white text-sm hover:opacity-90 transition-opacity shadow-sm flex items-center gap-2 ${selectedAppointment?.id === a.id ? 'z-[6]' : 'z-[5]'}`}
                          style={{ backgroundColor: a.status === 'Anulado' ? '#ef4444' : a.status === 'Instalado' ? '#22c55e' : a.status === 'Validado' ? '#15803d' : (a.color || '#3b82f6'), borderLeft: `8px solid ${EQUIPE_COLORS[a.equipe || 'equipe1']}`, top: `${a._top}px`, height: '26px', left: `${(a._ci / a._ct) * 100}%`, width: `calc(${100 / a._ct}% - 4px)`, boxShadow: selectedAppointment?.id === a.id ? '0 0 0 3px #0f172a' : undefined }}
                          data-testid={`appt-${a.id}`}>
                          {(a.status === 'Instalado' || a.status === 'Validado') && <span>&#10003; </span>}
                          {a.dossier_id && <FolderOpen size={12} className="inline-block" style={{ color: '#fbbf24' }} fill="#fbbf24" />}
                          <span className="font-medium">{a.client_name}</span>
                          <span className="opacity-80 text-xs">{a.start_time}</span>
                          {a._ct === 1 && a.address && <span className="opacity-70 text-xs truncate">{a.address}</span>}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
              );
            })()}

            {viewMode === "month" && (() => {
              const year = currentDate.getFullYear();
              const month = currentDate.getMonth();
              const firstDay = new Date(year, month, 1);
              const lastDay = new Date(year, month + 1, 0);
              const startDow = firstDay.getDay() === 0 ? 6 : firstDay.getDay() - 1;
              const totalDays = lastDay.getDate();
              const weeks = [];
              let day = 1 - startDow;
              while (day <= totalDays) {
                const week = [];
                for (let i = 0; i < 7; i++) {
                  week.push(day > 0 && day <= totalDays ? day : null);
                  day++;
                }
                weeks.push(week);
              }
              return (
                <table className="w-full border-collapse table-fixed" data-testid="planning-grid-month">
                  <thead className="sticky top-0 z-10 bg-white">
                    <tr>
                      {dayNames.map((d, i) => (
                        <th key={i} className="p-2 text-center text-xs text-slate-500 font-medium border-b border-slate-100 uppercase">{d}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {weeks.map((week, wi) => (
                      <tr key={wi}>
                        {week.map((d, di) => {
                          const cellDate = d ? new Date(year, month, d) : null;
                          const dateKey = cellDate ? formatDateKey(cellDate) : "";
                          const dayAppts = filteredAppointments.filter(a => a.date === dateKey);
                          const today = cellDate && isToday(cellDate);
                          return (
                            <td key={di} className={`border border-slate-400 p-1.5 align-top h-28 ${d ? '' : 'bg-slate-50/50'} ${today ? 'bg-sky-100 ring-2 ring-inset ring-blue-400' : ''}`}>
                              {d && (
                                <>
                                  <div onClick={() => { setCurrentDate(new Date(year, month, d)); setViewMode("day"); }}
                                    className={`text-sm font-semibold mb-1 cursor-pointer hover:underline ${today ? 'bg-blue-600 text-white w-7 h-7 rounded-full flex items-center justify-center' : 'text-slate-600 hover:text-blue-600'}`}>{d}</div>
                                  {dayAppts.slice(0, 3).map(a => (
                                    <div key={a.id}
                                      onClick={(e) => { e.stopPropagation(); setSelectedAppointment(a); }}
                                      className={`rounded px-1 py-0.5 mb-0.5 cursor-pointer text-white text-[10px] leading-tight truncate hover:opacity-90 relative ${selectedAppointment?.id === a.id ? 'z-[2]' : ''}`}
                                      style={{ backgroundColor: a.status === 'Anulado' ? '#ef4444' : a.status === 'Instalado' ? '#22c55e' : a.status === 'Validado' ? '#15803d' : (a.color || '#3b82f6'), borderLeft: `6px solid ${EQUIPE_COLORS[a.equipe || 'equipe1']}`, boxShadow: selectedAppointment?.id === a.id ? '0 0 0 3px #0f172a' : undefined }}
                                      data-testid={`appt-${a.id}`}>
                                      {a.dossier_id && <FolderOpen size={9} className="inline-block mr-0.5 align-text-top" style={{ color: '#fbbf24' }} fill="#fbbf24" />}
                                      {a.start_time} {a.client_name}
                                    </div>
                                  ))}
                                  {dayAppts.length > 3 && (
                                    <div className="text-[10px] text-slate-400 pl-1">+{dayAppts.length - 3} más</div>
                                  )}
                                </>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
              );
            })()}
          </div>
        </Card>

        {/* Detail Panel */}
        {selectedAppointment && (
          <AppointmentDetailPanel
            selectedAppointment={selectedAppointment}
            setSelectedAppointment={setSelectedAppointment}
            currentUser={currentUser}
            fetchAppointments={fetchAppointments}
            handleDelete={handleDelete}
            openEditDialog={openEditDialog}
            setConfirmCreateDossier={setConfirmCreateDossier}
            setShowTechDataDialog={setShowTechDataDialog}
          />
        )}
      </div>

      <AppointmentFormDialog
        open={showCreateDialog}
        onOpenChange={setShowCreateDialog}
        editing={!!editingAppointment}
        appt={newAppt}
        setAppt={setNewAppt}
        currentUser={currentUser}
        dossiers={dossiers}
        submitting={isSubmitting}
        onSubmit={editingAppointment ? handleUpdate : handleCreate}
        onCancel={() => { setShowCreateDialog(false); setEditingAppointment(null); }}
      />

      <TechDataDialog appointmentId={showTechDataDialog} onClose={() => setShowTechDataDialog(null)} />

      <CrearExpedienteDialog open={confirmCreateDossier} onOpenChange={setConfirmCreateDossier} appointment={selectedAppointment} onCreated={fetchAppointments} />
    </div>
  );
};

export default PlanningV2;
